/**
 * Durations people actually say, stored as minutes.
 *
 * A task that takes a week is 2400 minutes, and nobody types that or reads it
 * back. The format is Jira's — `3w 4d 6h 45m` — because it is the one most
 * people arriving at this field will already know, and because inventing a
 * second convention for the same idea helps nobody.
 *
 * Storage stays in minutes: the column does not change, nothing migrates, and
 * arithmetic over estimates stays trivial. This is a parser and a formatter at
 * the edges, in shared so the API and the browser cannot disagree about what
 * "2d" means.
 */
/**
 * A working day, not a calendar one.
 *
 * Eight hours, five days. "Two days of work" means two days *at work* — a
 * 24-hour day would make every estimate three times what its author intended,
 * and a 7-day week would do it again.
 */
export const MINUTES_PER_HOUR = 60;
export const HOURS_PER_DAY = 8;
export const DAYS_PER_WEEK = 5;
export const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;
export const MINUTES_PER_WEEK = MINUTES_PER_DAY * DAYS_PER_WEEK;
const UNIT_MINUTES = {
    m: 1,
    h: MINUTES_PER_HOUR,
    d: MINUTES_PER_DAY,
    w: MINUTES_PER_WEEK,
};
/** One `12h` or `45m` piece. Decimals allowed: "1.5h" is a thing people write. */
const PART = /^(\d+(?:\.\d+)?)([wdhm])$/;
/**
 * Read a duration.
 *
 * Empty means "no estimate", which is different from zero — a task nobody has
 * sized is not a task estimated at nothing.
 *
 * A bare number is minutes, matching what the field did before this existed,
 * so every estimate already stored keeps its meaning.
 */
export function parseDuration(input) {
    const text = input.trim().toLowerCase();
    if (text === "")
        return { ok: true, minutes: 0 };
    if (/^\d+(\.\d+)?$/.test(text)) {
        return { ok: true, minutes: Math.round(Number(text)) };
    }
    const parts = text.split(/\s+/);
    const seen = new Set();
    let total = 0;
    for (const part of parts) {
        const match = PART.exec(part);
        if (!match) {
            return {
                ok: false,
                error: `"${part}" is not a duration. Use w, d, h or m — for example 3d, 4h 30m, 2w.`,
            };
        }
        const [, amount, unit] = match;
        // A repeated unit is a typo, not a sum: "2h 3h" is far more likely to be a
        // mistake than somebody meaning five hours.
        if (seen.has(unit)) {
            return { ok: false, error: `"${unit}" appears twice. Write each unit once — 1w 2d, not 1w 1w.` };
        }
        seen.add(unit);
        total += Number(amount) * UNIT_MINUTES[unit];
    }
    return { ok: true, minutes: Math.round(total) };
}
/**
 * Write a duration back the way it would be typed.
 *
 * Largest unit first, zero parts omitted — 480 minutes is "1d", not
 * "0w 1d 0h 0m". Null and 0 are both nothing to show.
 */
export function formatDuration(minutes) {
    if (minutes === null || minutes === undefined || minutes <= 0)
        return "";
    let left = Math.round(minutes);
    const out = [];
    for (const [unit, size] of [
        ["w", MINUTES_PER_WEEK],
        ["d", MINUTES_PER_DAY],
        ["h", MINUTES_PER_HOUR],
        ["m", 1],
    ]) {
        const count = Math.floor(left / size);
        if (count > 0) {
            out.push(`${count}${unit}`);
            left -= count * size;
        }
    }
    return out.join(" ");
}
/** For a hint under the field: what the units mean here. */
export const DURATION_HINT = `w, d, h, m — a day is ${HOURS_PER_DAY}h and a week is ${DAYS_PER_WEEK}d.`;
//# sourceMappingURL=duration.js.map